import { useEffect, useState } from "react";
import { logger } from "@/lib/logger";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { fmtDateTime } from "@/lib/constants";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Mail, Send } from "lucide-react";

export default function DigestSettingsDialog({ open, onOpenChange }) {
  const [recipients, setRecipients] = useState("");
  const [enabled, setEnabled] = useState(true);
  const [lastSent, setLastSent] = useState(null);
  const [sending, setSending] = useState(false);

  useEffect(() => {
    if (open) {
      api.get("/digest/settings").then((d) => {
        setRecipients((d.recipients || []).join("\n"));
        setEnabled(d.enabled);
        setLastSent(d.last_sent_at);
      }).catch((e) => logger.error("Failed to load digest settings:", e));
    }
  }, [open]);

  const parseRecipients = () => recipients.split(/[\n,;]+/).map((r) => r.trim()).filter((r) => r.includes("@"));

  const save = async () => {
    try {
      const res = await api.put("/digest/settings", { recipients: parseRecipients(), enabled });
      setRecipients(res.recipients.join("\n"));
      toast.success("Digest settings saved");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.detail));
    }
  };

  const sendNow = async () => {
    setSending(true);
    try {
      await api.put("/digest/settings", { recipients: parseRecipients(), enabled });
      const res = await api.post("/digest/send", {});
      if (res.sent > 0) toast.success(`Digest sent to ${res.sent} recipient${res.sent > 1 ? "s" : ""}${res.failed ? ` (${res.failed} failed)` : ""}`);
      else toast.error("Digest send failed — check recipient addresses");
      setLastSent(new Date().toISOString());
    } catch (e) {
      toast.error(formatApiErrorDetail(e.detail));
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="digest-settings-modal">
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2">
            <Mail className="h-4 w-4 text-blue-600" />
            Daily Email Digest
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-5">
          <p className="text-xs text-slate-500">
            Every morning at <span className="font-semibold">07:00 IST</span> planners get a summary of delayed orders, capacity conflicts and each plant's on-time delivery.
          </p>
          <div className="flex items-center justify-between rounded-lg border border-slate-200 px-4 py-3">
            <div>
              <Label>Daily Digest Enabled</Label>
              {lastSent && <p className="text-[11px] text-slate-400 font-mono-data">Last sent {fmtDateTime(lastSent)}</p>}
            </div>
            <Switch checked={enabled} onCheckedChange={setEnabled} data-testid="digest-enabled-switch" />
          </div>
          <div className="space-y-1.5">
            <Label>Recipients (One per Line)</Label>
            <Textarea
              rows={4}
              value={recipients}
              onChange={(e) => setRecipients(e.target.value)}
              placeholder={"planner@company.com\nops-manager@company.com"}
              data-testid="digest-recipients-input"
            />
            <p className="text-[11px] text-slate-400">Tip: replace the demo @proplan.io addresses with real inboxes to actually receive the digest.</p>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={sendNow} disabled={sending} data-testid="digest-send-now">
              <Send className="h-3.5 w-3.5 mr-1.5" />
              {sending ? "Sending…" : "Send Now"}
            </Button>
            <Button onClick={save} data-testid="digest-save">Save Settings</Button>
          </DialogFooter>
        </div>
      </DialogContent>
    </Dialog>
  );
}
